import { defineComponent as _defineComponent } from 'vue'
import {
  BillingPackage,
  BillingPackageIntervalEnum,
  BillingQuotaDefinitionLookupKeyEnum,
  BillingSubscription,
  Company,
  Employee,
  EmployeeStatusEnum,
  SubscriptionQuote,
  TimesheetEntryStatusEnum,
} from '@/../api/v1';
import { billingApi } from '@/api';
import BillingForm from '@/components/billing/BillingForm';
import PlanConfirmationV2 from '@/components/billing/PlanConfirmationV2';
import Button from '@/components/buttons/Button';
import ActionDialog from '@/components/dialog/ActionDialog';
import Dialog from '@/components/dialog/Dialog';
import InputGroup from '@/components/form/InputGroup';
import StagedProgress from '@/components/graph/StagedProgress';
import LoadingDot from '@/components/loaders/LoadingDot';
import LoadingOverlay from '@/components/loaders/LoadingOverlay';
import localization from '@/i18n';
import { BillingDialogStageEnum } from '@/lib/billing/billing';
import {
  BillingDetailsFormType,
  buildBillingForm,
} from '@/lib/billing/billingFormSpec';
import {
  checkPlanChangeInterval,
  checkPlanChangeLevel,
  countEmployees,
  getPackageQuota,
  packageQuotaExceeded,
} from '@/lib/billing/billingFunctions';
import { withLoading } from '@/lib/composition/loading';
import { PlanChangeLevelEnum } from '@/lib/enum/billing/BillingEnum';
import { Icon } from '@/lib/enum/Icon';
import { Entity } from '@/lib/realtime/RealtimeEntity';
import {
  realtimeFindEntity,
  realtimeQuery,
  singleEntityRealtimeQuery,
} from '@/lib/realtime/weak/realtimeFunctions';
import { showError, showSuccess } from '@/plugins/ToastNotifications';
import { useRouteReactive } from '@/router/composition';
import { redirect } from '@/router/router';
import { routes } from '@/router/routes';
import store from '@/store';
import { viewport } from '@/util/windowFunctions';
import ManagePaymentMethods from '@/views/settings/common/ManagePaymentMethods.vue';
import BillingSummary from '@/views/sign-up/components/BillingSummary';
import ChooseBillingPlan from '@/views/sign-up/components/ChooseBillingPlan';
import useVuelidate from '@vuelidate/core';
import { computed, onMounted, reactive, ref, toRef, watch } from 'vue';
// eslint-disable-next-line import/no-unresolved
import { Location } from 'vue-router/types/router';


export default /*#__PURE__*/_defineComponent({
  __name: 'BillingPackageDialog',
  props: {
    value: { type: Boolean }
  },
  setup(__props: any) {



const currentRoute = useRouteReactive();

// use vuelidate state from child LocationForm.vue component
// https://vuelidate-next.netlify.app/advanced_usage.html#nested-validations
const v$ = useVuelidate();

const state = reactive({
  initialising: true,
  loading: false,
  upgrading: false,
  showPaymentErrorDialog: false,
  confirmTrialDialog: {
    show: false,
    action: null as Function | null,
    bodyText: '',
  },
  showPlanConfirmation: false,
  downgradeDialog: {
    show: false,
    locations: 0,
    shiftAreas: 0,
    employees: 0,
    clockedIn: 0,
  },
});

const billingPackages = realtimeQuery(Entity.BillingPackage)
  .where('allowsSignups', 'eq', true)
  .fetch();

const billingOnly = computed(
  () => store.state.billing.billingDialogState.isBillingOnly,
);
const restoreAccount = computed(
  () => store.state.billing.billingDialogState.isRestore,
);

const stage = computed(() => store.state.billing.billingDialogState.stage);

const billingDetails = singleEntityRealtimeQuery(Entity.BillingDetail);
const paymentMethods = realtimeQuery(Entity.BillingPaymentMethod).fetch();

const subscriptionInterval = ref<BillingPackageIntervalEnum>(
  BillingPackageIntervalEnum.Monthly,
);
const selectedPackage = ref<BillingPackage | null>(null);
// Store the current plan as an old plan for confirmation dialog
const oldPackage = ref<BillingPackage>(store.getters['billing/currentPackage']);
const quote = ref<SubscriptionQuote | null>(null);
const appliedOffers = ref<string[]>([]);

const form = computed<BillingDetailsFormType>(() => {
  const formTemplate = buildBillingForm(billingDetails.data);
  const billingFormValidations = v$.value.$getResultsForChild('billing-form');

  if (!billingFormValidations) {
    return formTemplate;
  }

  return Object.keys(formTemplate).reduce(
    (acc, key) => ({
      ...acc,
      [key]: billingFormValidations[key]?.$model || '',
    }),
    formTemplate,
  );
});

// Vuex stored values
const storeValues = computed<{
  currentCompany: Company | null;
  currentPackage: BillingPackage | null;
  currentSubscription: BillingSubscription;
  isTrialVersion: boolean;
  trialDaysLeft: number;
  timezone: string;
}>(() => ({
  currentCompany: store.getters.currentCompany,
  currentPackage: store.getters['billing/currentPackage'],
  currentSubscription: store.getters['billing/currentSubscription'],
  isTrialVersion: store.getters['billing/isTrialVersion'],
  trialDaysLeft: store.getters['billing/trialDaysLeft'],
  timezone: store.getters.timezone,
}));

const title = computed<string>(() => {
  if (restoreAccount.value) {
    return localization.tc('heading.restoreAccount');
  }
  if (billingOnly.value) {
    return localization.tc('heading.billingDetails');
  }
  return localization.tc('heading.choosePlan');
});
const submitButtonText = computed<string>(() => {
  if (stage.value === BillingDialogStageEnum.PaymentMethod) {
    return restoreAccount.value
      ? localization.tc('button.makePayment')
      : localization.tc('button.finish');
  }
  return localization.tc('button.continue');
});

const secondaryButtonText = computed<string>(() => {
  if (stage.value === BillingDialogStageEnum.BillingDetails) {
    if (restoreAccount.value) {
      return localization.tc('button.logout');
    }
    if (billingOnly.value) {
      return localization.tc('button.close');
    }
  }
  return localization.tc('button.goBack');
});

const stages = computed<{ label: string; complete: boolean }[]>(() => {
  return [
    ...(!billingOnly.value
      ? [
          {
            label: localization.tc('label.summary'),
            complete: true,
          },
        ]
      : []),
    {
      label: localization.tc('label.billing'),
      complete: [
        BillingDialogStageEnum.BillingDetails,
        BillingDialogStageEnum.PaymentMethod,
      ].includes(stage.value),
    },
    {
      label: localization.tc('label.payment'),
      complete: stage.value === BillingDialogStageEnum.PaymentMethod,
    },
  ];
});

const availablePackages = computed(() =>
  billingPackages.data.filter(
    (p) => p.id !== storeValues.value.currentPackage.id,
  ),
);

const canGoBack = computed(() => {
  if (stage.value === BillingDialogStageEnum.ChoosePlan) {
    return false;
  }

  if (restoreAccount.value || billingOnly.value) {
    return true;
  }

  if (stage.value === BillingDialogStageEnum.BillingDetails) {
    return availablePackages.value.length > 1;
  }

  return true;
});

const employeeHardLimitReachedForPlan = (
  employees: Employee[],
  billingPackage: BillingPackage,
): boolean => {
  return (
    countEmployees(employees) >=
    getPackageQuota(
      billingPackage,
      BillingQuotaDefinitionLookupKeyEnum.EmployeeHardLimit,
    )
  );
};

const checkDowngrade = async (callback: Function) => {
  // check locations
  // warn employee limit
  const employeeQuery = realtimeQuery(Entity.Employee)
    .whereIn('status', [EmployeeStatusEnum.Active, EmployeeStatusEnum.Pending])
    .fetch();

  await employeeQuery.promise;
  const employees = employeeQuery.data;

  const locationQuery = realtimeQuery(Entity.Location)
    .whereNotDeleted()
    .fetch();
  await locationQuery.promise;
  const locations = locationQuery.data.length;

  const shiftAreaQuery = realtimeQuery(Entity.ShiftArea)
    .whereNotDeleted()
    .fetch();
  await shiftAreaQuery.promise;
  const shiftAreas = shiftAreaQuery.data.length;

  const clockedInTimesheetsQuery = realtimeQuery(Entity.TimesheetEntry)
    .where('status', 'in', [
      TimesheetEntryStatusEnum.Break,
      TimesheetEntryStatusEnum.Started,
    ])
    .fetch();
  await clockedInTimesheetsQuery.promise;
  const clockedInTimesheets = clockedInTimesheetsQuery.data.length;
  if (
    employeeHardLimitReachedForPlan(employees, selectedPackage.value) ||
    packageQuotaExceeded(
      selectedPackage.value,
      BillingQuotaDefinitionLookupKeyEnum.LocationsLimit,
      locations,
    ) ||
    packageQuotaExceeded(
      selectedPackage.value,
      BillingQuotaDefinitionLookupKeyEnum.InProgressTimesheetEntriesLimit,
      clockedInTimesheets,
    ) ||
    packageQuotaExceeded(
      selectedPackage.value,
      BillingQuotaDefinitionLookupKeyEnum.ShiftAreasLimit,
      shiftAreas,
    )
  ) {
    state.downgradeDialog.show = true;
    state.downgradeDialog.locations = locations;
    state.downgradeDialog.shiftAreas = shiftAreas;
    state.downgradeDialog.employees = employees.length;
    state.downgradeDialog.clockedIn = clockedInTimesheets;
    return;
  }
  await callback();
};

const getConfirmBodyText = (): string => {
  const level = checkPlanChangeLevel(
    storeValues.value.currentPackage,
    selectedPackage.value,
  );
  const interval = checkPlanChangeInterval(
    storeValues.value.currentPackage.interval,
    selectedPackage.value.interval,
  );

  if (level === PlanChangeLevelEnum.LikeForLike) {
    return `signUp.confirm.trial.likeForLike.${interval}`;
  }
  return `signUp.confirm.trial.${level}`;
};

const getQuote = withLoading(toRef(state, 'loading'), async () => {
  if (billingOnly.value || !selectedPackage.value) {
    state.loading = false;
    return;
  }

  await billingApi
    .createSubscriptionQuote({
      createSubscriptionQuoteData: {
        billingPackageId: selectedPackage.value.id,
      },
    })
    .then(({ data }) => {
      quote.value = data;
    });
});

const setBillingDialogStage = async (newStage: BillingDialogStageEnum) => {
  store.commit('billing/SET_BILLING_DIALOG_STATE', { stage: newStage });
};

const choosePackage = async (
  billingPackage: BillingPackage = selectedPackage.value,
) => {
  selectedPackage.value = billingPackage;
  store.commit('billing/SET_BILLING_DIALOG_STATE', {
    chosenPackage: billingPackage.id,
  });
  const callback = async () => {
    await getQuote();
    await setBillingDialogStage(
      viewport.md
        ? BillingDialogStageEnum.BillingDetails
        : BillingDialogStageEnum.Summary,
    );
  };
  if (
    checkPlanChangeLevel(storeValues.value.currentPackage, billingPackage) ===
    PlanChangeLevelEnum.Downgrade
  ) {
    return checkDowngrade(callback);
  }
  await callback();
};

const addOffer = async (code: string) => {
  appliedOffers.value.push(code);
  await billingApi
    .createBillingCouponRedemption({
      createCouponRedemptionData: {
        code,
      },
    })
    .catch(() => {});
  await getQuote();
};

const removeOffer = (code: string) => {
  appliedOffers.value = appliedOffers.value.filter((c) => c !== code);
};

const close = () => {
  store.commit('billing/SET_BILLING_DIALOG_STATE', {
    show: false,
    stage: null,
    chosenPackage: null,
  });
};

const goBack = async () => {
  if (stage.value === BillingDialogStageEnum.Summary) {
    await setBillingDialogStage(BillingDialogStageEnum.ChoosePlan);
    return;
  }
  if (
    stage.value === BillingDialogStageEnum.BillingDetails &&
    restoreAccount.value
  ) {
    await redirect(routes.logout.route());
    close();
    return;
  }
  if (
    stage.value === BillingDialogStageEnum.BillingDetails &&
    billingOnly.value
  ) {
    close();
    return;
  }
  if (stage.value === BillingDialogStageEnum.BillingDetails) {
    await setBillingDialogStage(
      viewport.md
        ? BillingDialogStageEnum.ChoosePlan
        : BillingDialogStageEnum.Summary,
    );
    return;
  }
  if (stage.value === BillingDialogStageEnum.PaymentMethod) {
    await setBillingDialogStage(BillingDialogStageEnum.BillingDetails);
  }
};

const setBillingDetails = withLoading(
  toRef(state, 'loading'),
  async (callback: Function) => {
    const formData = form.value;
    if (!formData.address2) {
      delete formData.address2;
    }

    await billingApi
      .updateBillingDetails({
        updateBillingDetailsData: formData,
      })
      .then(() => {
        showSuccess(localization.tc('successMessage.billingDetailsUpdated'));
        callback();
      })
      .catch(() => {
        showError(
          localization.tc('errorMessage.genericUpdating', 1, {
            entity: localization.tc('entities.billingDetails').toLowerCase(),
          }),
        );
      });
  },
);

const handleBillingDetails = async () => {
  const details = billingDetails.data;
  if (details) {
    delete details.companyId;
    delete details.id;
  }

  const callback = async () => {
    await getQuote();
    await setBillingDialogStage(BillingDialogStageEnum.PaymentMethod);
  };

  if (!details || JSON.stringify(details) !== JSON.stringify(form.value)) {
    await setBillingDetails(callback);
    return;
  }
  await callback();
};

const upgradeBillingPlan = withLoading(toRef(state, 'loading'), async () => {
  await billingApi
    .updateBillingSubscription({
      updateBillingSubscriptionData: {
        billingPackageId: selectedPackage.value.id,
      },
    })
    .then(async () => {
      await store.dispatch('billing/fetchBillingSubscription');
    })
    .catch((e) => {
      showError(localization.tc('errorMessage.changePlan'));
      throw new Error(`Failed to upgrade billing subscription: ${e.message}`);
    });
});

const restoreCompany = async () => {
  await billingApi
    .restoreAccount()
    .then(async () => {
      await store.dispatch('billing/fetchBillingSubscription');
      store.commit('SET_COMPANY_STATUS_MODAL', false);
    })
    .catch((e) => {
      throw new Error(`Failed to restore account: ${e.message}`);
    });
};

const makePayment = withLoading(toRef(state, 'upgrading'), async () => {
  const promises = [];
  if (selectedPackage.value.id !== storeValues.value.currentPackage.id) {
    promises.push(upgradeBillingPlan);
  }
  if (restoreAccount.value) {
    promises.push(restoreCompany);
  }
  // eslint-disable-next-line no-restricted-syntax
  for (const promise of promises) {
    // eslint-disable-next-line no-await-in-loop
    await promise();
  }
  state.showPlanConfirmation = true;
});

const trialPackage = (billingPackage: BillingPackage) => {
  selectedPackage.value = billingPackage;
  const callback = () => {
    state.confirmTrialDialog.bodyText = localization.tc(getConfirmBodyText());
    state.confirmTrialDialog.action = () => {
      upgradeBillingPlan();
      state.showPlanConfirmation = true;
    };
    state.confirmTrialDialog.show = true;
  };
  if (
    checkPlanChangeLevel(storeValues.value.currentPackage, billingPackage) ===
    PlanChangeLevelEnum.Downgrade
  ) {
    return checkDowngrade(callback);
  }
  callback();
};

const submit = async () => {
  if (stage.value === BillingDialogStageEnum.Summary) {
    await setBillingDialogStage(BillingDialogStageEnum.BillingDetails);
  } else if (stage.value === BillingDialogStageEnum.BillingDetails) {
    await handleBillingDetails();
  } else if (stage.value === BillingDialogStageEnum.PaymentMethod) {
    await makePayment();
  }
};

const closePlanConfirmation = () => {
  state.showPlanConfirmation = false;
  close();
};

const goToRoute = (newRoute: Location) => {
  close();
  if (currentRoute.value.name !== newRoute.name) {
    redirect(newRoute);
  }
};

watch(
  () => store.state.billing.billingDialogState.chosenPackage,
  async (packageId) => {
    const billingPackage = await realtimeFindEntity(
      Entity.BillingPackage,
      Number(packageId),
    );

    if (billingPackage) {
      selectedPackage.value = billingPackage;
    }
  },
);

onMounted(async () => {
  await Promise.all([
    billingDetails.promise,
    paymentMethods.promise,
    billingPackages.promise,
  ]);

  subscriptionInterval.value = storeValues.value.currentPackage.interval;
  selectedPackage.value = store.state.billing.billingDialogState.chosenPackage
    ? billingPackages.data.find(
        (p) => p.id === store.state.billing.billingDialogState.chosenPackage,
      )
    : storeValues.value.currentPackage;

  if (
    availablePackages.value.length === 1 &&
    store.state.billing.billingDialogState.stage ===
      BillingDialogStageEnum.ChoosePlan
  ) {
    store.commit('billing/SET_BILLING_DIALOG_STATE', {
      chosenPackage: availablePackages.value[0].id,
      stage: BillingDialogStageEnum.BillingDetails,
    });
  }

  await getQuote();
  state.initialising = false;
});

return { __sfc: true,currentRoute, v$, state, billingPackages, billingOnly, restoreAccount, stage, billingDetails, paymentMethods, subscriptionInterval, selectedPackage, oldPackage, quote, appliedOffers, form, storeValues, title, submitButtonText, secondaryButtonText, stages, availablePackages, canGoBack, employeeHardLimitReachedForPlan, checkDowngrade, getConfirmBodyText, getQuote, setBillingDialogStage, choosePackage, addOffer, removeOffer, close, goBack, setBillingDetails, handleBillingDetails, upgradeBillingPlan, restoreCompany, makePayment, trialPackage, submit, closePlanConfirmation, goToRoute, BillingPackageIntervalEnum, BillingQuotaDefinitionLookupKeyEnum, BillingForm, PlanConfirmationV2, Button, ActionDialog, Dialog, InputGroup, StagedProgress, LoadingDot, LoadingOverlay, BillingDialogStageEnum, getPackageQuota, Icon, routes, viewport, ManagePaymentMethods, BillingSummary, ChooseBillingPlan }
}

})